import { useEffect, useState } from "react";
import { apiFetch } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { isNativeApp } from "@/lib/platform";
import {
  configureRevenueCat,
  getOfferings as rcGetOfferings,
  purchasePackage as rcPurchase,
  restorePurchases as rcRestore,
  showManageSubscriptions as rcManage,
  getCustomerInfo as rcCustomerInfo,
  extractRenewalStatus as rcExtractRenewal,
  isIOS as rcIsIOS,
} from "@/lib/revenuecat";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Check, Sparkles, RotateCw } from "lucide-react";

/**
 * The iOS subscription grid is ALLOWED to render ONLY these three self-service
 * plans. This is a defense-in-depth constant used at TWO points:
 *   1. When RevenueCat offerings are loaded, packages are filtered against
 *      this set so unmapped products can never reach component state.
 *   2. At render time inside the iOS grid, each package is re-checked so a
 *      corrupted state (e.g. hot reload) can never leak a card.
 * Enterprise / custom-sales plans are sold via web + Stripe only and must
 * never appear on iOS under any circumstance — even if RevenueCat's offering
 * or the /api/apple/config product map were misconfigured to include one.
 */
export const IOS_ALLOWED_PLAN_IDS = Object.freeze(["starter", "professional", "business"]);
const IOS_ALLOWED_SET = new Set(IOS_ALLOWED_PLAN_IDS);

/** True when the mapped plan id is a self-service iOS-safe plan. */
export function isIOSAllowedPlan(mappedPlanId) {
  return !!mappedPlanId && IOS_ALLOWED_SET.has(mappedPlanId);
}

function formatBytes(bytes) {
  if (!bytes) return "0 MB";
  const gb = bytes / (1024 * 1024 * 1024);
  if (gb >= 1) return `${gb.toFixed(gb % 1 === 0 ? 0 : 1)} GB`;
  return `${Math.round(bytes / (1024 * 1024))} MB`;
}

function UsageBar({ label, used, limit, unit = "", asBytes = false }) {
  const fmt = (v) => (asBytes ? formatBytes(v) : v.toLocaleString());
  const displayLimit = limit === null || limit === undefined ? "Unlimited" : fmt(limit);
  const pct = limit ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  const warn = pct >= 80;
  return (
    <div>
      <div className="flex justify-between text-sm mb-1.5">
        <span className="text-[#57534E]">{label}</span>
        <span className="font-medium">
          <span className={warn ? "text-[#E8712C]" : ""}>{fmt(used)}</span>
          <span className="text-[#A8A29E]"> / {displayLimit}{unit && !asBytes ? ` ${unit}` : ""}</span>
        </span>
      </div>
      <div className="h-2 rounded-full bg-[#F5EDE4] overflow-hidden">
        <div className={`h-full rounded-full transition-all ${warn ? "bg-[#E8712C]" : "bg-[#2C3A29]"}`}
             style={{ width: `${limit ? pct : 5}%` }} />
      </div>
    </div>
  );
}

export default function BillingTab() {
  const { currentOrgId, currentRole, user } = useAuth();
  const [data, setData] = useState(null);
  const [plans, setPlans] = useState([]);
  const [changing, setChanging] = useState(null);
  const [stripeCfg, setStripeCfg] = useState(null);
  // iOS native In-App Purchase state (RevenueCat / Apple StoreKit).
  const [iapReady, setIapReady] = useState(false);
  const [iapPackages, setIapPackages] = useState([]);
  const [iapProductToPlan, setIapProductToPlan] = useState({});
  const [iapError, setIapError] = useState(null);
  const [iapBuying, setIapBuying] = useState(null); // package identifier being purchased
  const [restoring, setRestoring] = useState(false);
  const [managing, setManaging] = useState(false);
  const [renewal, setRenewal] = useState(null); // { entitlement, expiresDate, willRenew, productIdentifier }
  const isAdmin = currentRole === "org_admin";
  const onIOS = rcIsIOS();

  useEffect(() => {
    if (!currentOrgId) return;
    apiFetch(`/subscriptions/${currentOrgId}`).then(setData).catch((e) => toast.error(e.message));
    apiFetch(`/plans`).then(setPlans).catch(() => {});
    apiFetch(`/stripe/config`).then(setStripeCfg).catch(() => setStripeCfg({ enabled: false, mode: "test" }));
    // Detect checkout return
    const params = new URLSearchParams(window.location.search);
    if (params.get("checkout") === "success") toast.success("Checkout completed — subscription syncing via webhook");
    if (params.get("checkout") === "cancelled") toast.info("Checkout cancelled");
  }, [currentOrgId]);

  // Configure RevenueCat and load Apple offerings only on the iOS native
  // build. The web bundle skips this entire block.
  useEffect(() => {
    if (!onIOS || !user?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const cfg = await apiFetch("/apple/config").catch(() => null);
        if (cfg?.product_to_plan) setIapProductToPlan(cfg.product_to_plan);
        if (!cfg?.enabled) {
          setIapError("iap_disabled");
          return;
        }
        const status = await configureRevenueCat(user.id);
        if (cancelled) return;
        if (!status.ready) {
          setIapError(status.reason || "not_ready");
          return;
        }
        const { packages, error } = await rcGetOfferings();
        if (cancelled) return;
        if (error) setIapError(error);
        // Defense-in-depth: hard-filter offerings down to the three
        // approved self-service plan ids at ingestion time. Even if RC's
        // offering or the product→plan map ever contained a custom-sales
        // package, it cannot reach component state and therefore cannot
        // render.
        const safePackages = (packages || []).filter((pkg) => {
          const product = pkg?.product || pkg?.storeProduct || {};
          const productId = product.identifier;
          const mapped = (cfg?.product_to_plan || {})[productId];
          return isIOSAllowedPlan(mapped);
        });
        setIapPackages(safePackages);
        setIapReady(true);
        // Load renewal/expiration details from Apple via RC.
        try {
          const ci = await rcCustomerInfo();
          if (!cancelled) setRenewal(rcExtractRenewal(ci));
        } catch { /* ignore */ }
      } catch (e) {
        if (!cancelled) setIapError(e?.message || "iap_init_failed");
      }
    })();
    // Refresh CustomerInfo + org subscription whenever the app resumes,
    // so returning from Apple's Manage Subscriptions sheet immediately
    // reflects any changes the user made (cancel, plan change, etc.).
    let removeResume;
    (async () => {
      if (!onIOS) return;
      try {
        const { App } = await import("@capacitor/app");
        const h = await App.addListener("appStateChange", async ({ isActive }) => {
          if (!isActive) return;
          try {
            const ci = await rcCustomerInfo();
            setRenewal(rcExtractRenewal(ci));
          } catch { /* ignore */ }
          try {
            const fresh = await apiFetch(`/subscriptions/${currentOrgId}`);
            setData(fresh);
          } catch { /* ignore */ }
        });
        removeResume = () => h.remove();
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; if (removeResume) removeResume(); };
  }, [onIOS, user?.id, currentOrgId]);

  const buyIAP = async (pkg) => {
    if (!isAdmin) return;
    const productId = pkg?.product?.identifier || pkg?.storeProduct?.identifier;
    setIapBuying(pkg?.identifier || productId);
    try {
      const result = await rcPurchase(pkg);
      // Notify backend for audit + expected plan (webhook is authoritative).
      const expiresMs = pkg?.product?.expiresDate || null;
      const hint = await apiFetch("/apple/sync-purchase", {
        method: "POST",
        body: JSON.stringify({
          organization_id: currentOrgId,
          product_id: productId,
          original_transaction_id: result?.customerInfo?.originalAppUserId || null,
          expires_date_ms: typeof expiresMs === "number" ? expiresMs : null,
        }),
      }).catch(() => null);
      toast.success("Subscription active — syncing with Apple…");
      // Poll for the webhook-driven update (up to ~15s).
      const expected = hint?.expected_plan_id;
      for (let i = 0; i < 8; i++) {
        // eslint-disable-next-line no-await-in-loop
        const fresh = await apiFetch(`/subscriptions/${currentOrgId}`).catch(() => null);
        if (fresh) {
          setData(fresh);
          if (!expected || fresh.subscription?.plan_id === expected) break;
        }
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, 2000));
      }
    } catch (e) {
      if (!e?.userCancelled && e?.code !== "PURCHASE_CANCELLED") {
        toast.error(e?.message || "Purchase failed");
      }
    } finally {
      setIapBuying(null);
    }
  };

  const restoreIAP = async () => {
    setRestoring(true);
    try {
      await rcRestore();
      toast.success("Purchases restored — subscription syncing");
      const fresh = await apiFetch(`/subscriptions/${currentOrgId}`);
      setData(fresh);
      try {
        const ci = await rcCustomerInfo();
        setRenewal(rcExtractRenewal(ci));
      } catch { /* ignore */ }
    } catch (e) {
      toast.error(e?.message || "Restore failed");
    } finally {
      setRestoring(false);
    }
  };

  const manageIAP = async () => {
    setManaging(true);
    try {
      const res = await rcManage();
      if (!res?.ok) throw new Error(res?.error || "Could not open Apple management");
      // We deliberately do NOT toast on success — the user is now in
      // Apple's sheet. The app-state listener above will refresh
      // CustomerInfo when they come back.
    } catch (e) {
      toast.error(e?.message || "Could not open Apple management");
    } finally {
      setManaging(false);
    }
  };

  const changePlan = async (planId) => {
    if (!isAdmin) return;
    setChanging(planId);
    try {
      // Web: Stripe checkout is the only self-service path for
      // starter/professional/business. Stripe is live in production; the
      // previous local-only "change without checkout" fallback has been
      // removed so users can never accidentally get an unpaid upgrade.
      const r = await apiFetch("/stripe/checkout", {
        method: "POST",
        body: JSON.stringify({ organization_id: currentOrgId, plan_id: planId }),
      });
      window.location.href = r.url;
    } catch (e) { toast.error(e.message); }
    finally { setChanging(null); }
  };

  const openPortal = async () => {
    try {
      const r = await apiFetch("/stripe/portal", {
        method: "POST",
        body: JSON.stringify({ organization_id: currentOrgId }),
      });
      window.location.href = r.url;
    } catch (e) { toast.error(e.message); }
  };

  if (!data) return <div className="text-[#57534E]">Loading subscription…</div>;
  const { subscription, plan, limits, usage } = data;
  const trialActive = subscription.status === "trial";
  const trialDaysLeft = subscription.trial_ends_at
    ? Math.max(0, Math.ceil((new Date(subscription.trial_ends_at) - new Date()) / (1000 * 60 * 60 * 24))) : 0;

  return (
    <div className="space-y-6" data-testid="billing-tab">
      <div className="rounded-2xl border border-[#E8E2D9] bg-white p-6">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs text-[#A8A29E] uppercase tracking-widest">Current plan</div>
            <div className="mt-1 flex items-center gap-3">
              <div className="font-display font-bold text-2xl tracking-tight">{plan.name}</div>
              <Badge className={trialActive ? "bg-[#FDE9D8] text-[#E8712C]" : "bg-[#E8F0E4] text-[#2C3A29]"}>
                {subscription.status}
              </Badge>
              {plan.is_featured && <Badge className="bg-[#E8712C] text-white"><Sparkles size={10} className="mr-1" /> Most popular</Badge>}
            </div>
            {trialActive && (
              <div className="text-sm text-[#E8712C] mt-2 font-medium">
                Trial: {trialDaysLeft} day{trialDaysLeft !== 1 ? "s" : ""} left
              </div>
            )}
          </div>
          <div className="text-right">
            {plan.contact_sales ? (
              <div className="font-display font-bold text-xl">Custom pricing</div>
            ) : (
              <div>
                <span className="font-display font-bold text-3xl tracking-tight">${plan.price_monthly ? Math.round(plan.price_monthly) : 0}</span>
                <span className="text-[#57534E]">/mo</span>
              </div>
            )}
          </div>
        </div>

        {onIOS && (
          <div className="mt-6 pt-6 border-t border-[#E8E2D9] space-y-3">
            {renewal?.expiresDate && (
              <div className="flex items-center justify-between gap-3 text-sm">
                <span className="text-[#57534E]">
                  {renewal.willRenew ? "Renews" : "Expires"}
                </span>
                <span className="font-medium text-[#2C3A29]" data-testid="ios-renewal-date">
                  {new Date(renewal.expiresDate).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}
                  {!renewal.willRenew && <span className="ml-2 text-[#C55A1F]">(cancelled — access until this date)</span>}
                </span>
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              <Button
                data-testid="ios-manage-subscription-btn"
                onClick={manageIAP}
                disabled={managing || !isAdmin}
                size="sm"
                className="rounded-full bg-[#E8712C] hover:bg-[#C55A1F]"
              >
                {managing ? "Opening…" : "Manage subscription"}
              </Button>
              <Button
                data-testid="ios-restore-inline-btn"
                onClick={restoreIAP}
                disabled={restoring || !iapReady}
                variant="outline"
                size="sm"
                className="rounded-full"
              >
                <RotateCw size={14} className={`mr-1.5 ${restoring ? "animate-spin" : ""}`} />
                {restoring ? "Restoring…" : "Restore"}
              </Button>
            </div>
            <p className="text-xs text-[#A8A29E]">
              Change plan or cancel renewal opens Apple's subscription page. Cancelling keeps your access until the renewal date above.
            </p>
          </div>
        )}
        {!onIOS && stripeCfg?.enabled && data.subscription.stripe_customer_id && (
          <Button data-testid="open-portal-btn" onClick={openPortal} variant="outline" size="sm" className="mt-4 rounded-full">
            Manage billing (Stripe portal)
          </Button>
        )}
      </div>

      <div className="rounded-2xl border border-[#E8E2D9] bg-white p-6">
        <h3 className="font-display font-semibold text-lg tracking-tight mb-4">Usage this period</h3>
        <div className="space-y-4">
          <UsageBar label="Team members" used={usage.users} limit={limits.user_limit} />
          <UsageBar label="Clients" used={usage.clients} limit={limits.client_limit} />
          <UsageBar label="Storage" used={usage.storage_bytes || 0} limit={limits.storage_bytes} asBytes />
          <UsageBar label="AI requests this month" used={usage.ai_requests} limit={limits.ai_monthly_limit} />
        </div>
      </div>

      {onIOS ? (
        <div className="rounded-2xl border border-[#E8E2D9] bg-white p-6" data-testid="ios-subscription-selection">
          <div className="flex items-start justify-between gap-3 flex-wrap mb-1">
            <h3 className="font-display font-semibold text-lg tracking-tight">Change plan</h3>
            <Button
              data-testid="ios-restore-purchases-btn"
              onClick={restoreIAP}
              disabled={restoring || !iapReady}
              variant="outline"
              size="sm"
              className="rounded-full"
            >
              <RotateCw size={14} className={`mr-1.5 ${restoring ? "animate-spin" : ""}`} />
              {restoring ? "Restoring…" : "Restore purchases"}
            </Button>
          </div>
          <p className="text-sm text-[#57534E] mb-6">
            Subscriptions are billed securely by Apple. Cancel anytime from Settings → Apple ID → Subscriptions on your iPhone.
          </p>

          {!iapReady && !iapError && (
            <div className="text-sm text-[#57534E]">Loading plans from the App Store…</div>
          )}
          {iapError && (
            <div className="p-3 rounded-lg bg-[#FDE9D8] text-xs text-[#C55A1F]" data-testid="ios-iap-error">
              Apple In-App Purchase isn't available right now. Please try again later or contact support.
            </div>
          )}

          {iapReady && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="ios-plan-grid">
                {["starter", "professional", "business"].map((slotPlanId) => {
                  const pkg = iapPackages.find((p) => {
                    const pid = (p?.product || p?.storeProduct || {}).identifier;
                    return iapProductToPlan[pid] === slotPlanId;
                  });
                  const product = pkg ? (pkg.product || pkg.storeProduct || {}) : {};
                  const productId = product.identifier || `com.clientdeskva.app.${slotPlanId}.monthly`;
                  const title = product.title || slotPlanId.charAt(0).toUpperCase() + slotPlanId.slice(1);
                  const priceString = product.priceString || null;
                  const current = slotPlanId === subscription.plan_id;
                  const busy = iapBuying === (pkg?.identifier || productId);
                  const unavailable = !pkg;
                  return (
                    <div
                      key={slotPlanId}
                      data-testid={`ios-iap-plan-${slotPlanId}`}
                      className={`p-5 rounded-2xl border-2 ${current ? "border-[#E8712C] bg-[#FDE9D8]/40" : "border-[#E8E2D9]"}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="font-display font-bold tracking-tight">{title}</div>
                        {current && <Badge className="bg-[#E8712C] text-white text-[10px]">CURRENT</Badge>}
                      </div>
                      <div className="mt-2 mb-4">
                        {priceString ? (
                          <>
                            <span className="font-display font-bold text-xl" data-testid={`ios-price-${slotPlanId}`}>{priceString}</span>
                            <span className="text-xs text-[#57534E]">/mo</span>
                          </>
                        ) : (
                          <span className="text-xs text-[#A8A29E]">Loading price from App Store…</span>
                        )}
                      </div>
                      {current ? (
                        <Button size="sm" disabled className="w-full rounded-full">Current plan</Button>
                      ) : unavailable ? (
                        <Button size="sm" disabled className="w-full rounded-full" data-testid={`ios-plan-unavailable-${slotPlanId}`}>
                          Unavailable in App Store
                        </Button>
                      ) : (
                        <Button
                          data-testid={`ios-iap-buy-${slotPlanId}`}
                          size="sm"
                          disabled={!isAdmin || busy}
                          onClick={() => buyIAP(pkg)}
                          className="w-full rounded-full bg-[#E8712C] hover:bg-[#C55A1F]"
                        >
                          {busy ? "Purchasing…" : (isAdmin ? "Subscribe" : "Admin only")}
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
              {iapPackages.length < 3 && (
                <div className="mt-4 p-3 rounded-lg bg-[#FDE9D8] text-xs text-[#C55A1F]" data-testid="ios-iap-diagnostic">
                  <b>App Store products incomplete.</b> RevenueCat returned {iapPackages.length} of 3 expected packages.
                  {iapPackages.length > 0 && (
                    <> Received: {iapPackages.map((p) => (p?.product || p?.storeProduct || {}).identifier).filter(Boolean).join(", ")}.</>
                  )}
                  <div className="mt-1">
                    This usually means one or more subscription products in App Store Connect are still in Missing Metadata or Waiting for Review.
                  </div>
                </div>
              )}
            </>
          )}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {iapPackages.map((pkg) => {
                const product = pkg?.product || pkg?.storeProduct || {};
                const productId = product.identifier;
                const mappedPlanId = iapProductToPlan[productId];
                // Defense-in-depth: only render packages that map to a
                // whitelisted self-service plan (starter / professional /
                // business). Enterprise / contact-sales plans are hard-
                // excluded from iOS at both ingestion time (see effect
                // above) AND here at render time.
                if (!isIOSAllowedPlan(mappedPlanId)) return null;
                const current = mappedPlanId === subscription.plan_id;
                const title = product.title || mappedPlanId || productId;
                const priceString = product.priceString || (product.price ? `$${product.price}` : "");
                const busy = iapBuying === (pkg?.identifier || productId);
                return (
                  <div
                    key={pkg.identifier || productId}
                    data-testid={`ios-iap-plan-${mappedPlanId || productId}`}
                    className={`p-5 rounded-2xl border-2 ${current ? "border-[#E8712C] bg-[#FDE9D8]/40" : "border-[#E8E2D9]"}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="font-display font-bold tracking-tight">{title}</div>
                      {current && <Badge className="bg-[#E8712C] text-white text-[10px]">CURRENT</Badge>}
                    </div>
                    <div className="mt-2 mb-4">
                      <span className="font-display font-bold text-xl">{priceString || "—"}</span>
                      <span className="text-xs text-[#57534E]">/mo</span>
                    </div>
                    {current ? (
                      <Button size="sm" disabled className="w-full rounded-full">Current plan</Button>
                    ) : (
                      <Button
                        data-testid={`ios-iap-buy-${mappedPlanId || productId}`}
                        size="sm"
                        disabled={!isAdmin || busy}
                        onClick={() => buyIAP(pkg)}
                        className="w-full rounded-full bg-[#E8712C] hover:bg-[#C55A1F]"
                      >
                        {busy ? "Purchasing…" : (isAdmin ? "Subscribe" : "Admin only")}
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          <p className="text-xs text-[#A8A29E] mt-6 leading-relaxed">
            Client-invoice payments — where a client pays you for real-world services — continue to work here as normal.
          </p>
        </div>
      ) : isNativeApp() ? (
        <div className="rounded-2xl border border-[#E8E2D9] bg-white p-6" data-testid="native-billing-note">
          <h3 className="font-display font-semibold text-lg tracking-tight mb-1">Manage your subscription on the web</h3>
          <p className="text-sm text-[#57534E]">
            To change your ClientDesk VA subscription, sign in at
            <span className="font-mono text-[#B85C1F]"> app.clientdeskva.com</span> from a browser.
          </p>
        </div>
      ) : (
      <div className="rounded-2xl border border-[#E8E2D9] bg-white p-6">
        <h3 className="font-display font-semibold text-lg tracking-tight mb-1">Change plan</h3>
        <p className="text-sm text-[#57534E] mb-6">Adjust your plan to fit your team.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {plans.map((p) => {
            const current = p.id === subscription.plan_id;
            return (
              <div key={p.id} data-testid={`billing-plan-${p.id}`}
                   className={`p-5 rounded-2xl border-2 ${current ? "border-[#E8712C] bg-[#FDE9D8]/40" : "border-[#E8E2D9]"}`}>
                <div className="flex items-start justify-between">
                  <div className="font-display font-bold tracking-tight">{p.name}</div>
                  {current && <Badge className="bg-[#E8712C] text-white text-[10px]">CURRENT</Badge>}
                </div>
                <div className="mt-2 mb-3">
                  {p.contact_sales
                    ? <div className="font-display font-bold text-lg">Custom</div>
                    : <div><span className="font-display font-bold text-xl">${Math.round(p.price_monthly || 0)}</span><span className="text-xs text-[#57534E]">/mo</span></div>}
                </div>
                <div className="text-xs text-[#57534E] space-y-0.5 mb-4">
                  <div>{p.user_limit ?? "Custom"} users</div>
                  <div>{p.client_limit?.toLocaleString() ?? "Custom"} clients</div>
                  <div>{p.storage_bytes ? formatBytes(p.storage_bytes) : "Custom"} storage</div>
                  {p.ai_monthly_limit && <div>{p.ai_monthly_limit} AI/mo</div>}
                </div>
                {p.contact_sales ? (
                  <a href="mailto:sales@clientdesk.va">
                    <Button size="sm" variant="outline" className="w-full rounded-full">Contact sales</Button>
                  </a>
                ) : current ? (
                  <Button size="sm" disabled className="w-full rounded-full">Current plan</Button>
                ) : (
                  <Button data-testid={`select-plan-${p.id}`} size="sm" disabled={!isAdmin || changing === p.id}
                          onClick={() => changePlan(p.id)}
                          className="w-full rounded-full bg-[#E8712C] hover:bg-[#C55A1F]">
                    {changing === p.id ? "Changing…" : (isAdmin ? "Select" : "Admin only")}
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      </div>
      )}

      <div className="text-xs text-[#A8A29E]">
        Plans and pricing are stored in the <code className="text-[#57534E]">plans</code> table and can be updated by the platform owner without a code deployment.
      </div>
    </div>
  );
}
